import React, { useState } from "react";

function LocationTreeNode({
  node,
  selectedId,
  onSelect,
  onAddChild,
  onEdit,
  onDelete,
  onDropProduct,
  level = 0,
}) {
  const [expanded, setExpanded] = useState(level < 2); // Auto-expand first 2 levels
  const [dragOver, setDragOver] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedId === node.id;

  const handleToggle = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const handleSelect = () => {
    onSelect(node);
  };

  const handleAddChild = (e) => {
    e.stopPropagation();
    onAddChild(node);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(node);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(node);
  };

  const handleDragOver = (e) => {
    if (!onDropProduct) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!onDropProduct) return;
    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      onDropProduct(data.productId, node.id, data.productName, node.name);
    } catch (err) {
      console.error("Drop error:", err);
    }
  };

  return (
    <div className="select-none">
      <div
        className={`flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer group transition-all ${
          dragOver
            ? "bg-blue-50 ring-2 ring-blue-300"
            : isSelected
            ? "bg-blue-100 text-blue-800"
            : "hover:bg-gray-100"
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={handleSelect}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Expand/collapse button */}
        <button
          onClick={handleToggle}
          className={`w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700 ${
            !hasChildren ? "invisible" : ""
          }`}
        >
          {expanded ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </button>

        {/* Folder/Location icon */}
        <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>

        {/* Name */}
        <span className="flex-1 truncate text-sm font-medium">{node.name}</span>

        {/* Root badge */}
        {node.is_root && (
          <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
            Root
          </span>
        )}

        {/* Action buttons (visible on hover, always reserve space to prevent dancing) */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleAddChild}
            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
            title="Sublocatie toevoegen"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button
            onClick={handleEdit}
            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
            title="Bewerken"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          {!node.is_root ? (
            <button
              onClick={handleDelete}
              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
              title="Verwijderen"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          ) : (
            <div className="w-6 h-6" /> // Placeholder to reserve space
          )}
        </div>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <LocationTreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              onDropProduct={onDropProduct}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function LocationTree({
  tree,
  selectedId,
  onSelect,
  onAddChild,
  onEdit,
  onDelete,
  onAddRoot,
  onDropProduct,
}) {
  if (!tree || tree.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
        <p className="text-sm">Nog geen locaties</p>
        <button
          onClick={onAddRoot}
          className="mt-3 text-sm text-blue-600 hover:text-blue-800"
        >
          + Eerste locatie toevoegen
        </button>
      </div>
    );
  }

  return (
    <div className="py-2">
      {tree.map((node) => (
        <LocationTreeNode
          key={node.id}
          node={node}
          selectedId={selectedId}
          onSelect={onSelect}
          onAddChild={onAddChild}
          onEdit={onEdit}
          onDelete={onDelete}
          onDropProduct={onDropProduct}
        />
      ))}
      <button
        onClick={onAddRoot}
        className="mt-2 ml-2 px-2 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded flex items-center gap-1"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Hoofdlocatie toevoegen
      </button>
    </div>
  );
}
